import React, { useEffect, useMemo, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { WIDGET_CATALOG, getWidgetMeta } from "@/lib/widgetCatalog";
import { getFavWidgets, setFavWidgets, getRecentWidgets, syncWidgetPrefs } from "@/lib/canvasUtils";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Star, Clock, LayoutTemplate, Trash2 } from "lucide-react";
import { writeWidgetDrag } from "@/lib/layoutEngine";
import { cn } from "@/lib/utils";

function WidgetTile({ w, onAdd, fav, onToggleFav, onDragStart, onDragEnd }) {
  return (
    <div className="relative group">
      <button data-testid={`add-widget-${w.type}`} onClick={() => onAdd(w.type)}
        draggable={!!onDragStart}
        onDragStart={onDragStart ? (e) => { writeWidgetDrag(e, { type: w.type }); onDragStart({ type: w.type }); } : undefined}
        onDragEnd={onDragEnd}
        title="Click to add, or drag onto the canvas / into a container"
        className="w-full flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] hover:border-[color:var(--brand-ocean)] hover:shadow-[var(--shadow-sm)] transition-all text-center cursor-grab active:cursor-grabbing">
        <w.icon className="w-4 h-4 text-[color:var(--brand-ocean)]" />
        <span className="text-[10px] leading-tight text-[color:var(--ink-2)]">{w.label}</span>
      </button>
      <button data-testid={`fav-widget-${w.type}`} onClick={(e) => { e.stopPropagation(); onToggleFav(w.type); }}
        title={fav ? "Remove from favorites" : "Add to favorites"}
        className={cn("absolute top-1 right-1 p-0.5 rounded transition-opacity",
          fav ? "opacity-100 text-amber-400" : "opacity-0 group-hover:opacity-100 text-[color:var(--ink-3)] hover:text-amber-400")}>
        <Star className="w-3 h-3" fill={fav ? "currentColor" : "none"} />
      </button>
    </div>
  );
}

export function WidgetLibrary({ onAdd, recentBump, onAddTemplate, templatesBump = 0, onDragStart = null, onDragEnd = null }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [favs, setFavs] = useState(getFavWidgets());
  const [cloudBump, setCloudBump] = useState(0);
  const [templates, setTemplates] = useState([]);
  // recentBump/cloudBump are external refresh triggers for the recents list
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const recents = useMemo(() => getRecentWidgets(), [recentBump, cloudBump]);

  useEffect(() => {
    if (!onAddTemplate) return;
    api.get("/widget-templates").then(({ data }) => setTemplates(data || [])).catch(() => {});
  }, [onAddTemplate, templatesBump]);
  const deleteTemplate = async (tpl) => {
    try { await api.delete(`/widget-templates/${tpl.id}`); setTemplates((t) => t.filter((x) => x.id !== tpl.id)); toast.success("Template deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // Hydrate favorites + recents from the backend so state syncs across devices.
  useEffect(() => {
    let alive = true;
    syncWidgetPrefs().then((res) => {
      if (!alive || !res) return;
      setFavs(res.favorites || []);
      setCloudBump((b) => b + 1);
    });
    return () => { alive = false; };
  }, []);

  const groups = useMemo(() => [...new Set(WIDGET_CATALOG.map((w) => w.group))], []);
  const toggleFav = (type) => {
    const next = favs.includes(type) ? favs.filter((t) => t !== type) : [...favs, type];
    setFavs(next); setFavWidgets(next);
  };

  const matches = (w) => {
    if (q && !w.label.toLowerCase().includes(q.toLowerCase()) && !w.type.includes(q.toLowerCase())) return false;
    if (cat !== "All" && w.group !== cat) return false;
    return true;
  };
  const filtered = WIDGET_CATALOG.filter(matches);
  const favWidgets = filtered.filter((w) => favs.includes(w.type));
  const recentWidgets = !q && cat === "All" ? recents.map((t) => WIDGET_CATALOG.find((w) => w.type === t)).filter(Boolean) : [];
  const grouped = filtered.reduce((acc, w) => { (acc[w.group] = acc[w.group] || []).push(w); return acc; }, {});

  return (
    <ScrollArea className="h-full">
      <div className="p-3">
        <div className="relative mb-2">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <Input data-testid="widget-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search widgets…"
            className="h-8 pl-8 text-xs bg-[color:var(--surface)]" />
        </div>
        <div className="flex flex-wrap gap-1 mb-3">
          {["All", ...groups].map((g) => (
            <button key={g} data-testid={`widget-cat-${g}`} onClick={() => setCat(g)}
              className={cn("text-[10px] px-2 py-0.5 rounded-full border transition-colors",
                cat === g ? "bg-[color:var(--brand-ocean)] text-white border-transparent" : "border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:border-[color:var(--brand-ocean)]")}>
              {g}
            </button>
          ))}
        </div>

        {templates.length > 0 && !q && cat === "All" && (
          <div className="mb-4">
            <div className="text-[11px] text-[color:var(--ink-3)] px-1 mb-1.5 flex items-center gap-1"><LayoutTemplate className="w-3 h-3 text-[color:var(--brand-mint)]" />Templates</div>
            <div className="space-y-1.5">
              {templates.map((t) => {
                const meta = getWidgetMeta(t.widget?.type);
                return (
                  <div key={t.id} className="relative group">
                    <button data-testid="template-tile" onClick={() => onAddTemplate(t)}
                      draggable={!!onDragStart}
                      onDragStart={onDragStart ? (e) => { writeWidgetDrag(e, { template_id: t.id }); onDragStart({ template: t }); } : undefined}
                      onDragEnd={onDragEnd}
                      className="w-full flex items-center gap-2 p-2 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] hover:border-[color:var(--brand-mint)] transition-all text-left cursor-grab active:cursor-grabbing">
                      <meta.icon className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-mint)]" />
                      <span className="text-[11px] leading-tight text-[color:var(--ink-2)] truncate">{t.name}</span>
                    </button>
                    <button data-testid="template-delete-btn" title="Delete template" onClick={(e) => { e.stopPropagation(); deleteTemplate(t); }}
                      className="absolute top-1.5 right-1.5 p-0.5 rounded opacity-0 group-hover:opacity-100 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {favWidgets.length > 0 && (
          <div className="mb-4">
            <div className="text-[11px] text-[color:var(--ink-3)] px-1 mb-1.5 flex items-center gap-1"><Star className="w-3 h-3 text-amber-400" fill="currentColor" />Favorites</div>
            <div className="grid grid-cols-2 gap-2">
              {favWidgets.map((w) => <WidgetTile key={w.type} w={w} onAdd={onAdd} fav onToggleFav={toggleFav} onDragStart={onDragStart} onDragEnd={onDragEnd} />)}
            </div>
          </div>
        )}

        {recentWidgets.length > 0 && (
          <div className="mb-4">
            <div className="text-[11px] text-[color:var(--ink-3)] px-1 mb-1.5 flex items-center gap-1"><Clock className="w-3 h-3" />Recently used</div>
            <div className="grid grid-cols-2 gap-2">
              {recentWidgets.map((w) => <WidgetTile key={w.type} w={w} onAdd={onAdd} fav={favs.includes(w.type)} onToggleFav={toggleFav} onDragStart={onDragStart} onDragEnd={onDragEnd} />)}
            </div>
          </div>
        )}

        {filtered.length === 0 && <div className="text-xs text-[color:var(--ink-3)] px-1 py-4 text-center">No widgets match “{q}”</div>}

        {Object.entries(grouped).map(([group, ws]) => (
          <div key={group} className="mb-4">
            <div className="text-[11px] text-[color:var(--ink-3)] px-1 mb-1.5">{group}</div>
            <div className="grid grid-cols-2 gap-2">
              {ws.map((w) => <WidgetTile key={w.type} w={w} onAdd={onAdd} fav={favs.includes(w.type)} onToggleFav={toggleFav} onDragStart={onDragStart} onDragEnd={onDragEnd} />)}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}
